<template>
  <SideBar />

  <main id="sidenavmain" class="main-content position-relative max-height-vh-100 h-100 border-radius-lg">
    <NavBar />

    <div class="py-4 container-fluid mb-4">
      <div class="col-lg-12 position-relative mt-n3">

        <div class="ts-head d-flex flex-column flex-xl-row justify-content-between align-items-xl-center gap-3 mb-3">
          <div class="ts-head-left d-flex align-items-center gap-2">
            <h6 class="ts-title mb-0">Exam categories</h6>
            <span class="ts-count badge bg-light text-secondary border px-2 py-1" v-if="!isLoading">{{ totalItems }}</span>
            <span class="ts-count badge bg-light text-secondary border px-2 py-1" v-else>...</span>
          </div>
          <div class="ts-head-right d-flex flex-wrap gap-2 justify-content-start justify-content-xl-end align-items-center">
            <div class="ts-search-wrap flex-grow-1 flex-md-grow-0" style="min-width: 220px;">
              <svg class="ts-search-ico" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
              </svg>
              <input v-model="searchQuery" @input="debounceSearch" class="ts-search-input w-100" placeholder="Search..." />
            </div>
            <button class="ts-btn-add d-flex align-items-center justify-content-center flex-grow-1 flex-md-grow-0" @click="openAddModal">
              Add New
              <svg width="11" height="11" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24" class="ms-1">
                <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
              </svg>
            </button>
          </div>
        </div>

        <div class="card ts-card">
          <div class="ts-card-body">
            <div v-if="!isLoading && paginatedItems.length === 0" class="ts-empty">
              <p class="ts-empty-title">No exam categories yet</p>
              <p class="ts-empty-sub">Add a category like "Mid-term" or "End of term"</p>
            </div>

            <div v-show="isLoading || paginatedItems.length > 0" class="table-responsive d-none d-md-block">
              <table class="ts-table" data-ts-mobile="cards">
                <thead>
                <tr>
                  <th>Category</th>
                  <th class="text-center" style="width:120px">Report order</th>
                  <th class="text-center" style="width:130px">Actions</th>
                </tr>
                </thead>
                <tbody>
                <template v-if="isLoading">
                  <tr v-for="n in pageSize" :key="'sk'+n" class="ts-skel-row">
                    <td data-label="Category"><div class="ts-skel" style="width:120px;height:12px"></div></td>
                    <td data-label="Report order" class="text-center"><div class="ts-skel" style="width:30px;height:12px;margin:0 auto"></div></td>
                    <td data-label="Actions"><div class="ts-skel" style="width:90px;height:28px;border-radius:8px;margin:0 auto"></div></td>
                  </tr>
                </template>
                <template v-else>
                  <tr v-for="item in paginatedItems" :key="item.id">
                    <td data-label="Category"><strong>{{ item.category }}</strong></td>
                    <td data-label="Report order" class="text-center">{{ item.reportorder }}</td>
                    <td data-label="Actions">
                      <div class="ts-actions justify-content-center">
                        <button class="ts-btn-edit" @click="editItem(item)" title="Edit">
                          <svg width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24"><path d="M12 20h9"/><path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z"/></svg>
                        </button>
                        <button class="ts-btn-suspend" @click="deleteItem(item)" title="Delete">
                          <svg width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg>
                        </button>
                      </div>
                    </td>
                  </tr>
                </template>
                </tbody>
              </table>
            </div>

            <div class="d-md-none ts-mobile-list" v-show="isLoading || paginatedItems.length > 0">
              <template v-if="!isLoading">
                <div v-for="item in paginatedItems" :key="'m'+item.id" class="ts-card-m">
                  <div class="ts-card-top">
                    <div class="ts-card-info">
                      <div class="ts-name">{{ item.category }}</div>
                      <div class="ts-email">Order: {{ item.reportorder }}</div>
                    </div>
                  </div>
                  <div class="ts-card-acts">
                    <button class="ts-btn-edit-full" @click="editItem(item)">Edit</button>
                    <button class="ts-btn-suspend-full" @click="deleteItem(item)">Delete</button>
                  </div>
                </div>
              </template>
            </div>

            <div v-if="!isLoading && totalItems > 0" class="ts-pagination">
              <span class="ts-pg-info">Showing {{ paginationFrom }} to {{ paginationTo }} of {{ totalItems }}</span>
              <div class="ts-pg-btns">
                <button class="ts-pg-btn" @click="currentPage--" :disabled="currentPage === 1">‹</button>
                <button v-for="p in pageButtons" :key="p" class="ts-pg-btn" :class="{ 'ts-pg-active': p === currentPage }" @click="currentPage = p">{{ p }}</button>
                <button class="ts-pg-btn" @click="currentPage++" :disabled="currentPage === lastPage">›</button>
              </div>
            </div>
          </div>
        </div>

      </div>
    </div>
    <AppFooter />
  </main>

  <!-- ═══ ADD/EDIT MODAL ═══ -->
  <div v-if="showModal" class="tw-overlay z-index-10000" @mousedown.self="closeModal">
    <div class="tw-modal" style="max-width: 400px;">
      <div class="tw-header">
        <h5 class="tw-title">{{ isEditing ? 'Edit category' : 'Add category' }}</h5>
        <button class="tw-close" @click="closeModal">&times;</button>
      </div>
      <div class="tw-body" style="background: var(--surface-2, #f8fafc);">
        <div class="card p-3 shadow-sm border-0">
          <div class="row g-3">
            <div class="col-12">
              <label class="tw-label">Name <span class="tw-req">*</span></label>
              <input v-model="form.category" type="text" class="tw-input" maxlength="50" placeholder="e.g. Mid-term exam" />
            </div>
            <div class="col-12">
              <label class="tw-label">Report order <span class="tw-req">*</span></label>
              <input v-model.number="form.reportorder" type="number" min="0" max="99" class="tw-input" />
              <p class="text-xs text-muted mb-0 mt-1">Where this appears on printed reports. Lower appears first.</p>
            </div>
          </div>
        </div>
      </div>
      <div class="tw-footer d-flex justify-content-end gap-2 p-3 bg-white border-top">
        <button class="tw-btn tw-btn-ghost" @click="closeModal">Cancel</button>
        <button class="tw-btn tw-btn-primary" @click="submitForm" :disabled="isSubmitting">
          {{ isSubmitting ? 'Saving...' : (isEditing ? 'Save' : 'Add') }}
        </button>
      </div>
    </div>
  </div>

</template>

<script>
/* eslint-disable */
import SideBar from "@/fragments/exam/AcademicsSidebar.vue";
import NavBar from "@/fragments/exam/AcademicsNavBar.vue";
import AppFooter from "@/fragments/Footer.vue";
import apiService from '@/apis/apiService';
import swal from 'sweetalert';

export default {
  name: "ExamCategoriesPage",
  components: { SideBar, NavBar, AppFooter },

  data() {
    return {
      items: [],
      isLoading: true,
      searchQuery: '',
      currentPage: 1,
      pageSize: 10,
      searchTimer: null,

      showModal: false,
      isEditing: false,
      isSubmitting: false,
      form: this.blankForm(),
    };
  },

  computed: {
    filteredItems() {
      if (!this.searchQuery) return this.items;
      const q = this.searchQuery.toLowerCase();
      return this.items.filter(i => (i.category || '').toLowerCase().includes(q));
    },
    totalItems() { return this.filteredItems.length; },
    lastPage() { return Math.ceil(this.totalItems / this.pageSize) || 1; },
    paginatedItems() {
      const start = (this.currentPage - 1) * this.pageSize;
      return this.filteredItems.slice(start, start + this.pageSize);
    },
    paginationFrom() { return this.totalItems === 0 ? 0 : (this.currentPage - 1) * this.pageSize + 1; },
    paginationTo() { return Math.min(this.currentPage * this.pageSize, this.totalItems); },
    pageButtons() {
      const n = this.lastPage;
      return Array.from({ length: n }, (_, i) => i + 1);
    },
  },

  mounted() {
    this.fetchItems();
    if (window.innerWidth >= 1024) {
      import('perfect-scrollbar').then((m) => {
        const PS = m.default;
        const c = document.getElementById('sidenavmain');
        if (c) new PS(c);
      });
    }
  },

  methods: {
    async fetchItems() {
      this.isLoading = true;
      try {
        const res = await apiService.apiClient.get('/api/academics/exam-categories');
        this.items = Array.isArray(res.data) ? res.data : [];
      } catch (e) {
        this.items = [];
        swal("Error", this.readErr(e, "Could not load categories"), "error");
      } finally {
        this.isLoading = false;
      }
    },

    debounceSearch() {
      clearTimeout(this.searchTimer);
      this.searchTimer = setTimeout(() => { this.currentPage = 1; }, 300);
    },

    openAddModal() {
      this.isEditing = false;
      this.form = this.blankForm();
      this.showModal = true;
    },

    editItem(item) {
      this.form = { id: item.id, category: item.category, reportorder: item.reportorder };
      this.isEditing = true;
      this.showModal = true;
    },

    closeModal() { this.showModal = false; },

    async submitForm() {
      if (!this.form.category || !this.form.category.trim()) {
        swal("Required", "Category name is required", "warning");
        return;
      }
      if (this.form.reportorder == null) {
        swal("Required", "Report order is required", "warning");
        return;
      }
      this.isSubmitting = true;
      try {
        if (this.isEditing) {
          await apiService.apiClient.put(`/api/academics/exam-categories/${this.form.id}`, this.form);
        } else {
          await apiService.apiClient.post('/api/academics/exam-categories', this.form);
        }
        swal("Saved", "Category saved", "success");
        this.closeModal();
        this.fetchItems();
      } catch (e) {
        swal("Error", this.readErr(e, "Could not save"), "error");
      } finally {
        this.isSubmitting = false;
      }
    },

    async deleteItem(item) {
      const ok = await swal({
        title: "Delete this category?",
        text: `"${item.category}" will be removed.`,
        icon: "warning",
        buttons: ["Cancel", "Delete"],
        dangerMode: true,
      });
      if (!ok) return;
      try {
        await apiService.apiClient.delete(`/api/academics/exam-categories/${item.id}`);
        swal("Deleted", "Category removed", "success");
        this.fetchItems();
      } catch (e) {
        swal("Cannot delete", this.readErr(e, "Delete failed"), "error");
      }
    },

    blankForm() { return { id: null, category: '', reportorder: 1 }; },
    readErr(e, fb) { return (e?.response?.data?.querystatus) || fb; },
  },
};
</script>

<style scoped src="@/assets/css/ts-table.css"></style>